import React, { useEffect, useMemo, useState } from "react";
import { Tooltip } from "./tooltip";

/**
 * ASCII Weather Art Component
 *
 * Contains all weather ASCII art graphics and helper functions for the weather app.
 */

// ============================================================================
// Weather Condition Types
// ============================================================================

export type WeatherCondition =
  | "sunny"
  | "cloudy"
  | "partly_cloudy"
  | "rainy"
  | "thunderstorm"
  | "snowy"
  | "foggy";

// ============================================================================
// Large ASCII Weather Art (for current weather display)
// ============================================================================

export const WEATHER_ASCII_ART: Record<WeatherCondition, string[]> = {
  sunny: [
    `

       \\   |   /       
         .---.         
      -- (   ) --      
         \`---'         
       /   |   \\       
                       
    `,
    `
      \\    |    /      
       \\   |   /       
      .---.      
   ---  (   ) ---    
      \`---'      
       /   |   \\       
      /    |    \\      
    `,
  ],

  partly_cloudy: [
    `
      \\  /             
    _ /"".-.           
      \\_(   ).         
      /(___(__)        
    `,
    `
       \\  /            
     _ /"".-.          
       \\_(   ).        
       /(___(__)       
    `,
    `
      \\   /           
    _ /"".-.          
      \\_(   ).         
      /(___(__)        
    `,
  ],

  cloudy: [
    `

         .--.          
      .-(    ).        
     (___.__)__)       
    `,
    `

        .--.           
     .-(    ).         
    (___.__)__)        
    `,
  ],

  rainy: [
    `
         .--.          
      .-(    ).        
     (___.__)__)       
      ʻ   ʻ   ʻ        
     ʻ   ʻ   ʻ     

     
    `,
    `
         .--.          
      .-(    ).        
     (___.__)__)       
       ʻ   ʻ   ʻ       
      ʻ   ʻ   ʻ        
       ʻ   ʻ   ʻ      

    `,
    `
         .--.          
      .-(    ).        
     (___.__)__)       
       ʻ   ʻ   ʻ       
        ʻ   ʻ   ʻ      
      ʻ   ʻ   ʻ   ʻ    
       ʻ   ʻ   ʻ      
    `,
    `
          .--.          
       .-(    ).        
      (___.__)__)       
       ʻ ʻ ʻ ʻ ʻ       
      ʻ ʻ ʻ ʻ ʻ        
       ʻ ʻ ʻ ʻ ʻ       

    `,
  ],

  thunderstorm: [
    `
         .--.          
      .-(    ).        
     (___.__)__)       
       ⚡ʻ ʻ⚡ʻ         
      ʻ ʻ⚡ʻ ʻ          

    `,
    `
         .--.          
      .-(    ).        
     (___.__)__)       
       ⚡ʻ⚡   ⚡ʻ        
      ʻ   ⚡ʻ   ʻ       
    ʻ   ⚡ʻ   ʻ       

    `,
    `
         .--.          
      .-(    ).        
     (___.__)__)       
       ⚡⚡⚡⚡⚡         
      ʻ ʻ ʻ ʻ ʻ        
      ⚡⚡⚡⚡⚡         
     ʻ ʻ ʻ ʻ ʻ       
    `,
  ],

  snowy: [
    `
         .--.          
      .-(    ).        
     (___.__)__)       
       * * * *         
      * * * *        
      

    `,
    `
         .--.          
      .-(    ).        
     (___.__)__)       
       *  *  *         
      *  *  *          
     *  *  *         

    `,
    `
         .--.          
      .-(    ).        
     (___.__)__)       
      * * * * *        
       * * * *         
       * * * * *       
       * * * * *       
    `,
  ],

  foggy: [
    `
      _ - _ - _ -      
       _ - _ - _       
      - _ - _ - _      
       _ - _ - _       
    `,
    `
       - _ - _ - _     
      _ - _ - _ -      
       - _ - _ - _     
      _ - _ - _ -      
    `,
    `
      _ - _ - _ -      
     - _ - _ - _ -     
      _ - _ - _ -      
     - _ - _ - _ -     
    `,
  ],
};

// ============================================================================
// Mini Weather Icons (for forecasts) - Pure ASCII
// ============================================================================

export const WEATHER_ICONS: Record<WeatherCondition, string> = {
  sunny: "*",
  cloudy: "=",
  partly_cloudy: "~",
  rainy: ":",
  thunderstorm: "!",
  snowy: "*",
  foggy: "-",
};

// ============================================================================
// Wind Direction Helper
// ============================================================================

export const getWindDirection = (degrees: number): string => {
  const directions = [
    "N",
    "NNE",
    "NE",
    "ENE",
    "E",
    "ESE",
    "SE",
    "SSE",
    "S",
    "SSW",
    "SW",
    "WSW",
    "W",
    "WNW",
    "NW",
    "NNW",
  ];
  const index = Math.round(degrees / 22.5) % 16;
  return directions[index];
};

// ============================================================================
// ASCII Weather Display Component
// ============================================================================

interface AsciiWeatherDisplayProps {
  weatherFormat: "F" | "C";
  condition: WeatherCondition;
  temp: number;
  description: string;
  className?: string;
}

export const AsciiWeatherDisplay: React.FC<AsciiWeatherDisplayProps> = ({
  weatherFormat,
  condition,
  temp,
  description,
  className = "",
}) => {
  const frames = useMemo(
    () => WEATHER_ASCII_ART[condition] || WEATHER_ASCII_ART.cloudy,
    [condition]
  );
  const [frameIndex, setFrameIndex] = useState(0);

  useEffect(() => {
    setFrameIndex(0);
    const interval = window.setInterval(
      () => {
        setFrameIndex((prev) => (prev + 1) % frames.length);
      },
      frames.length > 1 ? 500 : 1000
    );

    return () => window.clearInterval(interval);
  }, [frames]);

  const art = frames[frameIndex] || frames[0];
  const artNormalized = useMemo(() => {
    const lines = (art || "").split("\n");
    return lines.join("\n");
  }, [art]);

  return (
    <div className={`font-mono ${className}`}>
      <div className="flex flex-col items-center justify-center">
        {/* ASCII Art */}
        <div
          className="mb-2 overflow-hidden flex items-center justify-center"
          style={{ height: "8rem", width: "28ch" }}
        >
          <pre className="text-[0.75rem] sm:text-sm leading-tight select-none whitespace-pre text-center m-0">
            {artNormalized}
          </pre>
        </div>

        {/* Temperature */}
        <div className="border-4 border-black px-6 py-3 bg-white">
          <div className="text-3xl sm:text-4xl font-bold text-center">
            {temp}°{weatherFormat.toLowerCase()}
          </div>
        </div>

        {/* Description */}
        <div className="mt-3 text-xs sm:text-sm uppercase tracking-wide opacity-60">
          {description}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Weather Stats Grid Component
// ============================================================================

interface WeatherStat {
  label: string;
  value: string;
  icon?: string;
}

interface AsciiWeatherStatsProps {
  stats: WeatherStat[];
  className?: string;
}

export const AsciiWeatherStats: React.FC<AsciiWeatherStatsProps> = ({
  stats,
  className = "",
}) => {
  return (
    <div
      className={`grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 ${className}`}
    >
      {stats.map((stat, index) => (
        <div
          key={index}
          className="border-2 border-black p-2 sm:p-3 text-center"
        >
          <div className="text-[0.65rem] sm:text-xs uppercase opacity-60 mb-1">
            {stat.label}
          </div>
          <div className="text-sm sm:text-base font-bold">{stat.value}</div>
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// Hourly Forecast Item Component
// ============================================================================

interface HourlyItemProps {
  time: string;
  condition: WeatherCondition;
  temp: number;
  precipProb: number;
}

export const AsciiHourlyItem: React.FC<HourlyItemProps> = ({
  time,
  condition,
  temp,
  precipProb,
}) => {
  const icon = WEATHER_ICONS[condition] || WEATHER_ICONS.cloudy;

  return (
    <div className="flex flex-col items-center min-w-[60px] sm:min-w-[70px] border-r-2 border-black last:border-r-0 px-2 py-2">
      <div className="text-[0.65rem] sm:text-xs font-bold mb-2 uppercase">
        {time}
      </div>
      <div className="text-xl sm:text-2xl mb-2">{icon}</div>
      <div className="text-sm sm:text-base font-bold mb-1">{temp}°</div>
      <div className="text-[0.6rem] sm:text-xs opacity-60">{precipProb}%</div>
    </div>
  );
};

// ============================================================================
// Daily Forecast Row Component
// ============================================================================

interface DailyItemProps {
  day: string;
  date?: string;
  condition: WeatherCondition;
  description: string;
  high: number;
  low: number;
  precipProb: number;
}

export const AsciiDailyItem: React.FC<DailyItemProps> = ({
  day,
  date,
  condition,
  description,
  high,
  low,
  precipProb,
}) => {
  const icon = WEATHER_ICONS[condition] || WEATHER_ICONS.cloudy;

  return (
    <div className="border-b-2 border-black last:border-b-0 p-3 sm:p-4 hover:bg-[#fbf1c7] transition-colors">
      <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-4 items-center">
        {/* Day */}
        <div className="sm:col-span-3">
          <div className="text-xs sm:text-sm font-bold uppercase">{day}</div>
          {date && (
            <div className="text-[0.65rem] sm:text-xs opacity-60">{date}</div>
          )}
        </div>

        {/* Icon & Description */}
        <div className="sm:col-span-5 flex items-center gap-2 sm:gap-3">
          <div className="text-2xl sm:text-3xl">{icon}</div>
          <div className="text-xs sm:text-sm">{description}</div>
        </div>

        {/* Temp Range */}
        <div className="sm:col-span-2 flex items-center gap-2">
          <div className="text-sm sm:text-base font-bold">{high}°</div>
          <div className="text-xs sm:text-sm opacity-60">/ {low}°</div>
        </div>

        {/* Precipitation */}
        <div className="sm:col-span-2 flex items-center gap-1">
          <span className="text-xs sm:text-sm opacity-60">{precipProb}%</span>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Temperature Unit Toggle Component
// ============================================================================

interface TempUnitToggleProps {
  unit: "F" | "C";
  onChange: (unit: "F" | "C") => void;
  className?: string;
}

export const AsciiTempUnitToggle: React.FC<TempUnitToggleProps> = ({
  unit,
  onChange,
  className = "",
}) => {
  return (
    <div className={`flex border-2 border-black ${className}`}>
      <button
        onClick={() => onChange("F")}
        className={`px-3 py-2 text-xs sm:text-sm font-bold transition-colors ${
          unit === "F" ? "bg-black text-white" : "bg-white hover:bg-[#fbf1c7]"
        }`}
      >
        °F
      </button>
      <button
        onClick={() => onChange("C")}
        className={`px-3 py-2 text-xs sm:text-sm font-bold border-l-2 border-black transition-colors ${
          unit === "C" ? "bg-black text-white" : "bg-white hover:bg-[#fbf1c7]"
        }`}
      >
        °C
      </button>
    </div>
  );
};

// ============================================================================
// Temperature Forecast Graph Component (inspired by sports game flow)
// ============================================================================

interface TempForecastProps {
  hourlyData: Array<{ time: string; temp: number }>;
  unit: "F" | "C";
  className?: string;
}

export const AsciiTempForecast: React.FC<TempForecastProps> = ({
  hourlyData,
  unit,
  className = "",
}) => {
  if (hourlyData.length === 0) return null;

  // Hover state and dynamic width
  const [hoveredHour, setHoveredHour] = React.useState<number | null>(null);
  const [mousePosition, setMousePosition] = React.useState<{
    x: number;
    y: number;
  } | null>(null);
  const [currentHourPosition, setCurrentHourPosition] = React.useState<{
    x: number;
    y: number;
  } | null>(null);
  const [isPointerDown, setIsPointerDown] = React.useState(false);
  const [charsPerHour, setCharsPerHour] = React.useState(3);
  const [labelInterval, setLabelInterval] = React.useState(1); // How many hours between labels
  const graphRef = React.useRef<HTMLPreElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Take first 24 hours
  const data = hourlyData.slice(0, 24);

  // Find the current hour index by matching against actual current time
  const getCurrentHourIndex = (): number => {
    const now = new Date();
    const currentHour = now.getHours(); // 0-23

    // Convert current hour to 12-hour format with AM/PM (matching backend format like "10AM", "11PM")
    const currentHour12 = currentHour % 12 || 12; // Convert 0 to 12
    const ampm = currentHour >= 12 ? "PM" : "AM";
    const currentTimeStr = `${currentHour12}${ampm}`;

    // Find the index in data that matches current time
    const index = data.findIndex((d) => d.time === currentTimeStr);
    return index >= 0 ? index : 0; // Default to 0 if not found
  };

  const currentHourIndex = getCurrentHourIndex();

  // Find min and max temps for scaling
  const temps = data.map((d) => d.temp);
  const minTemp = Math.min(...temps);
  const maxTemp = Math.max(...temps);
  const tempRange = maxTemp - minTemp;

  // Number of rows for the graph (height)
  const graphHeight = 10;

  // Responsive breakpoints for character density and label spacing.
  // This keeps x-axis positions stable until a breakpoint is crossed.
  const breakpointConfig = React.useMemo(
    () => [
      { maxWidth: 480, charsPerHour: 2, labelInterval: 4 },
      { maxWidth: 640, charsPerHour: 3, labelInterval: 3 },
      { maxWidth: 800, charsPerHour: 4, labelInterval: 2 },
      { maxWidth: 960, charsPerHour: 5, labelInterval: 2 },
      { maxWidth: 1120, charsPerHour: 6, labelInterval: 1 },
      { maxWidth: Number.POSITIVE_INFINITY, charsPerHour: 8, labelInterval: 1 },
    ],
    []
  );

  // Effective graph width in characters: each hour is 1 glyph plus spacing.
  // (n - 1) * charsPerHour + 1 keeps character grid stable between breakpoints.
  const graphWidthChars = (data.length - 1) * charsPerHour + 1;

  // Calculate optimal characters per hour based on container width
  React.useEffect(() => {
    const updateCharsPerHour = () => {
      if (!containerRef.current) return;

      const containerWidth = containerRef.current.offsetWidth;
      // Subtract padding and temp scale width (approximately 40-50px)
      const availableWidth = containerWidth - 80;

      // Choose discrete breakpoint to avoid continuous shifts.
      const config =
        breakpointConfig.find((bp) => availableWidth <= bp.maxWidth) ||
        breakpointConfig[breakpointConfig.length - 1];

      if (config.charsPerHour !== charsPerHour) {
        setCharsPerHour(config.charsPerHour);
      }
      if (config.labelInterval !== labelInterval) {
        setLabelInterval(config.labelInterval);
      }
    };

    updateCharsPerHour();
    window.addEventListener("resize", updateCharsPerHour);

    return () => window.removeEventListener("resize", updateCharsPerHour);
  }, [charsPerHour, labelInterval, breakpointConfig]);

  // Calculate position for current hour tooltip
  React.useEffect(() => {
    const updateCurrentHourPosition = () => {
      if (!graphRef.current || currentHourIndex < 0) return;

      const rect = graphRef.current.getBoundingClientRect();
      // Calculate the character width based on the total width of the graph
      const charWidth = rect.width / graphWidthChars;

      // Position at the center of the current hour's column
      const hourCenterChar =
        currentHourIndex * charsPerHour + Math.floor(charsPerHour / 2);

      // X position: left edge of graph + position in graph + temp scale offset (approx 40px)
      const x = rect.left + hourCenterChar * charWidth + 40;

      // Y position: middle of the graph vertically
      const y = rect.top + rect.height / 2;

      setCurrentHourPosition({ x, y });
    };

    updateCurrentHourPosition();

    // Update on scroll or resize
    window.addEventListener("scroll", updateCurrentHourPosition, true);
    window.addEventListener("resize", updateCurrentHourPosition);

    return () => {
      window.removeEventListener("scroll", updateCurrentHourPosition, true);
      window.removeEventListener("resize", updateCurrentHourPosition);
    };
  }, [charsPerHour, currentHourIndex, graphWidthChars]);

  const createGraph = (hoverHour: number | null = null) => {
    // Map each temp to a row position
    const positions = data.map((d) => {
      if (tempRange === 0) return Math.floor(graphHeight / 2);
      const normalized = (d.temp - minTemp) / tempRange;
      return graphHeight - 1 - Math.floor(normalized * (graphHeight - 1));
    });

    // Build the graph row by row
    const rows: string[] = [];
    for (let row = 0; row < graphHeight; row++) {
      let line = "";

      for (let hourIndex = 0; hourIndex < data.length; hourIndex++) {
        const currPos = positions[hourIndex];
        const prevPos = hourIndex > 0 ? positions[hourIndex - 1] : null;
        const isHovered = hoverHour === hourIndex;
        const isCurrentHour = hourIndex === currentHourIndex;
        // Only show current hour line when not hovering anything
        const showCurrentHourLine = isCurrentHour && hoverHour === null;

        // Draw the data point for this hour
        if (currPos === row) {
          // We're on the actual temperature point
          const prev = hourIndex > 0 ? positions[hourIndex - 1] : currPos;
          const next =
            hourIndex < data.length - 1 ? positions[hourIndex + 1] : currPos;

          if (hourIndex > 0 && hourIndex < data.length - 1) {
            // Check for peaks and valleys
            if (currPos < prev && currPos < next) {
              line += isHovered ? "█" : "*"; // Peak (warmest)
            } else if (currPos > prev && currPos > next) {
              line += isHovered ? "█" : "*"; // Valley (coldest)
            } else {
              line += isHovered ? "█" : ".";
            }
          } else {
            line += isHovered ? "█" : ".";
          }
        } else if (isHovered || showCurrentHourLine) {
          // Draw vertical line for hover or current hour (only when not hovering)
          line += "|";
        } else if (prevPos !== null) {
          // Draw connecting line from previous hour
          const minPos = Math.min(prevPos, currPos);
          const maxPos = Math.max(prevPos, currPos);

          if (row > minPos && row < maxPos) {
            line += ":"; // Vertical connecting line
          } else {
            line += " ";
          }
        } else {
          line += " ";
        }

        // Add spacing after each hour (except the last)
        if (hourIndex < data.length - 1) {
          const spacingChars = charsPerHour - 1;

          for (let i = 0; i < spacingChars; i++) {
            if (
              !isHovered &&
              !showCurrentHourLine &&
              currPos === row &&
              positions[hourIndex + 1] === row
            ) {
              // Draw connecting horizontal lines if at the same temperature
              line += ".";
            } else if (!isHovered && !showCurrentHourLine && prevPos !== null) {
              // Check if we're on connecting path for spacing columns too
              const minPos = Math.min(currPos, positions[hourIndex + 1]);
              const maxPos = Math.max(currPos, positions[hourIndex + 1]);
              if (row >= minPos && row <= maxPos) {
                line += ".";
              } else {
                line += " ";
              }
            } else {
              line += " ";
            }
          }
        }
      }

      rows.push(line);
    }

    return rows;
  };

  const graphRows = createGraph(hoveredHour);

  // Shared helper to update hover from a screen point
  const updateHoverFromPoint = (clientX: number, clientY: number) => {
    if (!graphRef.current) return;

    const rect = graphRef.current.getBoundingClientRect();
    const x = clientX - rect.left;

    // Calculate character width (approximate for monospace)
    const charWidth = rect.width / graphWidthChars;
    const charIndex = Math.floor(x / charWidth);

    // Convert character index to hour index
    const hourIndex = Math.floor(charIndex / charsPerHour);

    if (hourIndex >= 0 && hourIndex < data.length) {
      setHoveredHour(hourIndex);
      setMousePosition({ x: clientX, y: clientY });
    } else {
      setHoveredHour(null);
      setMousePosition(null);
    }
  };

  // Mouse move handler (desktop hover)
  const handleMouseMove = (e: React.MouseEvent<HTMLPreElement>) => {
    updateHoverFromPoint(e.clientX, e.clientY);
  };

  const handleMouseLeave = () => {
    setHoveredHour(null);
    setMousePosition(null);
  };

  // Pointer handlers (touch/pen/mobile) with drag-to-hover support
  const handlePointerDown = (e: React.PointerEvent<HTMLPreElement>) => {
    if (e.pointerType !== "mouse") e.preventDefault();
    setIsPointerDown(true);
    updateHoverFromPoint(e.clientX, e.clientY);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLPreElement>) => {
    if (e.pointerType === "mouse" && !isPointerDown) {
      // Let mouse hover be handled by onMouseMove to avoid duplicate events
      return;
    }
    if (isPointerDown) {
      if (e.pointerType !== "mouse") e.preventDefault();
      updateHoverFromPoint(e.clientX, e.clientY);
    }
  };

  const handlePointerUp = () => {
    setIsPointerDown(false);
  };

  const handlePointerCancel = () => {
    setIsPointerDown(false);
    handleMouseLeave();
  };

  // Create temperature scale on the left
  const tempScale = Array.from({ length: graphHeight }, (_, i) => {
    const temp = maxTemp - (i / (graphHeight - 1)) * tempRange;
    return Math.round(temp);
  });

  return (
    <div ref={containerRef} className={`font-mono ${className}`}>
      <div className="text-xs sm:text-sm font-bold mb-3 text-center">
        Temperature Forecast (24 Hours)
      </div>

      {/* Legend */}
      <div className="text-[0.6rem] sm:text-xs opacity-60 mb-2 text-center">
        (1 dot = 1 hour, * = peak/valley, hover for details)
      </div>

      {/* Graph with Tooltip */}
      <div className="overflow-x-auto border-2 border-black p-2 sm:p-3 bg-white">
        {/* Current hour tooltip (shown when not hovering) */}
        <Tooltip
          show={
            hoveredHour === null &&
            currentHourPosition !== null &&
            currentHourIndex >= 0
          }
          position={currentHourPosition || undefined}
          content={
            <div className="text-xs sm:text-sm font-bold whitespace-nowrap">
              NOW - {data[currentHourIndex]?.time} -{" "}
              {data[currentHourIndex]?.temp}°{unit}
            </div>
          }
        >
          <div></div>
        </Tooltip>

        {/* Hover tooltip */}
        <Tooltip
          show={hoveredHour !== null && mousePosition !== null}
          position={mousePosition || undefined}
          content={
            hoveredHour !== null ? (
              <div className="text-xs sm:text-sm font-bold whitespace-nowrap">
                {data[hoveredHour].time} - {data[hoveredHour].temp}°{unit}
              </div>
            ) : null
          }
        >
          <div>
            <pre
              ref={graphRef}
              className="text-[0.5rem] sm:text-xs leading-tight cursor-crosshair"
              style={{ touchAction: "pan-y" }}
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerCancel}
            >
              {graphRows.map((row, i) => (
                <div key={i}>
                  <span className="inline-block w-8 text-right mr-2 opacity-60">
                    {tempScale[i]}°
                  </span>
                  <span>{row}</span>
                </div>
              ))}
            </pre>
          </div>
        </Tooltip>

        {/* Time labels for hours (responsive) */}
        <div className="mt-2 overflow-x-auto">
          <div className="flex text-[0.5rem] sm:text-xs leading-tight font-mono">
            {/* Spacer matching the temperature scale width */}
            <span className="inline-block w-8 mr-2 flex-shrink-0"></span>
            {/* Labels container matching graph width */}
            <div
              className="relative shrink-0"
              style={{ width: `${graphWidthChars}ch` }}
            >
              {data.map((hour, index) => {
                // Always show first, last, and current hour
                // For others, show based on interval
                const isFirst = index === 0;
                const isLast = index === data.length - 1;
                const isOnInterval = index % labelInterval === 0;
                const shouldShow = isFirst || isLast || isOnInterval;

                if (!shouldShow) return null;

                // Calculate position based on character position in the graph
                // Each hour takes charsPerHour characters, center the label on the hour
                const charPosition =
                  index * charsPerHour + Math.floor(charsPerHour / 2);
                const percentPosition = (charPosition / graphWidthChars) * 100;

                return (
                  <div
                    key={index}
                    className={`absolute ${
                      index === currentHourIndex
                        ? "text-red-600 font-bold"
                        : "opacity-60"
                    }`}
                    style={{
                      left: `${percentPosition}%`,
                      transform: "translateX(-50%)",
                    }}
                  >
                    {hour.time}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="mt-3 flex justify-around text-[0.65rem] sm:text-xs">
        <div>
          <span className="opacity-60">High: </span>
          <span className="font-bold">
            {maxTemp}°{unit}
          </span>
        </div>
        <div>
          <span className="opacity-60">Low: </span>
          <span className="font-bold">
            {minTemp}°{unit}
          </span>
        </div>
        <div>
          <span className="opacity-60">Range: </span>
          <span className="font-bold">{tempRange}°</span>
        </div>
      </div>
    </div>
  );
};
